import { useLazyQuery, useMutation } from '@apollo/client';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Box } from '@ovComponents/1-primative/box/box';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import { Button } from '@ovComponents/2-component/button/button';
import { Checkbox } from '@ovComponents/2-component/checkbox/checkbox';
import { CircularProgress } from '@ovComponents/2-component/circularProgress/circularProgress';
import { Dialog, DialogContent } from '@ovComponents/2-component/dialog/dialog';
import { IconButton } from '@ovComponents/2-component/iconButton/iconButton';
import { TableCell, TableRow } from '@ovComponents/2-component/table/table';
import { useGlobalToast } from '@providers/globalToastProvider';
import { useThemeTokens } from '@providers/themeTokenProvider';
import React, { useState } from 'react';
import {
  FETCH_BLOTTER_DETAIL,
  FETCH_SUB_TRADE_REQUESTS_FULL,
} from '../queries';
import { SECONDARY_TEXT_SX, TRUNCATE_SX } from '../styles';
import {
  formatMoney,
  getPortfolioName,
  getPortfolioStatus,
  type PriorityKey,
} from '../utils';
import {
  BUCKET_SUB_TRADE_REQUESTS,
  CREATE_SUB_TRADE_REQUESTS,
} from '../utils/mutations';
import PortfolioRowDetail, { openClientDetails } from './portfolioRowDetail';
import TradeBlotter from './tradeBlotter';

export const getPriorityConfig = (sys: any) =>
  ({
    urgent: {
      label: 'ACTION',
      color: sys.color.negative,
      bg: sys.color.negativeVariant,
      border: sys.color.negative,
      rank: 0,
    },
    review: {
      label: 'REVIEW',
      color: sys.color.warning,
      bg: sys.color.warningVariant,
      border: sys.color.warning,
      rank: 1,
    },
    ok: {
      label: 'OK',
      color: sys.color.positive,
      bg: 'transparent',
      border: sys.color.positive,
      rank: 2,
    },
  }) as const;
export type { PriorityKey };

const getPortfolioRowSx = (
  sys: ReturnType<typeof useThemeTokens>['sys'],
  report: { _priority: PriorityKey },
  isPinned: boolean,
) => {
  const priorityConfig = getPriorityConfig(sys);
  const cfg =
    priorityConfig[report._priority as PriorityKey] || priorityConfig.ok;

  return {
    cursor: 'pointer',
    bgcolor: isPinned ? sys.color.primaryContainer : cfg.bg,
    borderLeft: '3px solid',
    borderLeftColor: isPinned ? sys.color.primary : cfg.border,
    outline: isPinned ? `1px solid ${sys.color.primary}` : undefined,
    outlineOffset: isPinned ? -1 : undefined,
    '&:hover': { backgroundColor: sys.color.neutralHover },
    '&:hover .row-actions': { opacity: 1 },
  };
};

const PortfolioRow = React.memo(
  ({
    report,
    selected,
    onToggleSelect,
    isExpanded,
    onSetExpanded,
    organizationId,
    bulkTradingProcess,
    onTradeComplete,
    isPinned = false,
  }: {
    report: any;
    selected: boolean;
    onToggleSelect: (reportId: string) => void;
    isExpanded: boolean;
    onSetExpanded: (id: string | null) => void;
    organizationId: string;
    bulkTradingProcess: string | undefined;
    onTradeComplete: () => void;
    isPinned?: boolean;
  }) => {
    const { sys } = useThemeTokens();
    const { showToast } = useGlobalToast();
    const [localOpen, setLocalOpen] = useState(false);
    const [showTradeBlotter, setShowTradeBlotter] = useState(false);
    const status = getPortfolioStatus(report);
    const [createSubTradeRequests] = useMutation(CREATE_SUB_TRADE_REQUESTS);
    const [bucketSubTradeRequests] = useMutation(BUCKET_SUB_TRADE_REQUESTS);

    const [fetchBlotterDetail, { data: blotterData, loading: blotterLoading }] =
      useLazyQuery(FETCH_BLOTTER_DETAIL, {
        fetchPolicy: 'cache-and-network',
        errorPolicy: 'all',
      });

    const [fetchActiveSTRs, { data: strData, loading: strLoading }] =
      useLazyQuery(FETCH_SUB_TRADE_REQUESTS_FULL, {
        fetchPolicy: 'cache-and-network',
        errorPolicy: 'all',
      });

    const blotterReport =
      blotterData?.fetchPortfolioReports?.portfolioReports?.[0];
    const activeSubTradeRequests =
      strData?.fetchSubTradeRequests?.subTradeRequests || [];

    const openBlotter = () => {
      setShowTradeBlotter(true);
      const isGoal = report.modelPortfolioType === 'GOAL';
      const objectId = isGoal ? report.object?.id : report.object?.id;
      fetchBlotterDetail({
        variables: {
          input: {
            filter: {
              organizationId,
              ...(isGoal ? { goalId: objectId } : { subAccountId: objectId }),
            },
            pagination: { perPage: 1, offSet: 0 },
          },
        },
      });
      fetchActiveSTRs({
        variables: {
          input: {
            filter: {
              ...(isGoal ? { goalId: objectId } : { subAccountId: objectId }),
              states: ['INITIATED', 'READY', 'REQUESTED'],
            },
            pagination: { perPage: 100, offSet: 0 },
          },
        },
      });
    };

    const open = localOpen || isExpanded;

    // Parent deep-link sets `isExpanded` without toggling `localOpen`.
    const handleToggleOpen = () => {
      if (isExpanded) {
        onSetExpanded(null);
        setLocalOpen(false);
        return;
      }
      setLocalOpen((prev) => !prev);
    };

    const driftPct = report._drift ?? null;

    const cashPct =
      report.marketValueCents > 0
        ? ((report.cashAvailableForTradeCents || 0) / report.marketValueCents) *
          100
        : 0;
    const cashTarget = (report.expectedCashPercentage || 0) * 100;
    const cashOverUnder = cashPct - cashTarget;

    const statusDotColor =
      status.label === 'On Track'
        ? sys.color.positive
        : status.label === 'Drift'
          ? sys.color.warning
          : status.label === 'Liquidate' || status.label === 'Transfer'
            ? sys.color.negative
            : sys.color.onDisabled;

    const priorityConfig = getPriorityConfig(sys);

    return (
      <>
        <TableRow
          id={`portfolio-row-${report.id}`}
          sx={getPortfolioRowSx(sys, report, isPinned)}
          onClick={handleToggleOpen}
        >
          <TableCell
            sx={{ width: '36px', py: 0.5 }}
            onClick={(e) => e.stopPropagation()}
          >
            <Checkbox
              checked={selected}
              onChange={() => onToggleSelect(report.id)}
            />
          </TableCell>
          <TableCell sx={{ width: '28px', py: 0.5 }}>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                handleToggleOpen();
              }}
              size='small'
            >
              {open ? (
                <KeyboardArrowUpIcon sx={{ fontSize: 16 }} />
              ) : (
                <KeyboardArrowDownIcon sx={{ fontSize: 16 }} />
              )}
            </IconButton>
          </TableCell>
          {/* Priority Badge */}
          <TableCell sx={{ width: '50px', py: 0.5 }}>
            {(() => {
              const cfg =
                priorityConfig[report._priority as PriorityKey] ||
                priorityConfig.ok;
              return (
                <Typography
                  variant='labelSmall'
                  sx={{ fontWeight: 800, color: cfg.color }}
                >
                  {cfg.label}
                </Typography>
              );
            })()}
          </TableCell>
          {/* Name + Type */}
          <TableCell sx={{ width: 'auto', minWidth: '180px', py: 0.5 }}>
            <Box display='flex' alignItems='center' gap={0.75}>
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  bgcolor: statusDotColor,
                  flexShrink: 0,
                }}
              />
              <Box overflow='hidden'>
                <Typography
                  variant='bodySmall'
                  weight='bold'
                  sx={{ lineHeight: 1.3, ...TRUNCATE_SX }}
                >
                  {getPortfolioName(report)}
                </Typography>
                <Typography variant='labelSmall' sx={SECONDARY_TEXT_SX}>
                  {report.modelPortfolioType} • {status.label}
                </Typography>
              </Box>
            </Box>
          </TableCell>
          {/* Cash — with % indicator */}
          <TableCell right sx={{ width: '120px', py: 0.5 }}>
            <Typography variant='bodySmall' sx={{ textAlign: 'right' }}>
              {formatMoney(report.cashAvailableForTradeCents)}
            </Typography>
            <Typography
              variant='bodySmall'
              sx={{
                textAlign: 'right',
                color:
                  cashOverUnder < -2
                    ? sys.color.negative
                    : cashOverUnder > 5
                      ? sys.color.warning
                      : sys.color.onSurfaceVariant,
              }}
            >
              {cashPct.toFixed(1)}%{' '}
              {cashTarget > 0 ? `/ ${cashTarget.toFixed(1)}%` : ''}
            </Typography>
          </TableCell>
          {/* Market Value */}
          <TableCell right sx={{ width: '110px', py: 0.5 }}>
            <Typography
              variant='bodySmall'
              weight='bold'
              sx={{ textAlign: 'right' }}
            >
              {formatMoney(report.marketValueCents)}
            </Typography>
          </TableCell>
          {/* Drift — inline bar */}
          <TableCell sx={{ width: '100px', py: 0.5 }}>
            {driftPct !== null ? (
              <Box display='flex' alignItems='center' gap={0.5}>
                <Box
                  sx={{
                    flex: 1,
                    height: 4,
                    bgcolor: sys.color.disabled,
                    borderRadius: 2,
                    overflow: 'hidden',
                    maxWidth: 50,
                  }}
                >
                  <Box
                    sx={{
                      width: `${Math.min(driftPct * 10, 100)}%`,
                      height: '100%',
                      borderRadius: 2,
                      bgcolor:
                        driftPct > 5
                          ? sys.color.negative
                          : driftPct > 2
                            ? sys.color.warning
                            : sys.color.positive,
                    }}
                  />
                </Box>
                <Typography
                  variant='bodySmall'
                  sx={{ ...SECONDARY_TEXT_SX, minWidth: 30 }}
                >
                  {driftPct.toFixed(1)}%
                </Typography>
              </Box>
            ) : (
              <Typography
                variant='bodySmall'
                sx={{ color: sys.color.onDisabled }}
              >
                —
              </Typography>
            )}
          </TableCell>
          {/* Pending Trades */}
          <TableCell center sx={{ width: '60px', py: 0.5 }}>
            {report.pendingSubTradeRequests > 0 ? (
              <Box
                onClick={(e) => {
                  e.stopPropagation();
                  if (!open) handleToggleOpen();
                }}
                sx={{
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  px: 0.75,
                  py: 0.25,
                  borderRadius: '6px',
                  bgcolor: sys.color.primaryVariant,
                  '&:hover': { opacity: 0.85 },
                }}
              >
                <Typography
                  variant='bodySmall'
                  weight='bold'
                  sx={{ color: sys.color.primary }}
                >
                  {report.pendingSubTradeRequests}
                </Typography>
              </Box>
            ) : (
              <Typography
                variant='labelSmall'
                sx={{ color: sys.color.onDisabled }}
              >
                —
              </Typography>
            )}
          </TableCell>
          {/* Actions — appear on hover */}
          <TableCell
            sx={{ width: '140px', py: 0.5 }}
            onClick={(e) => e.stopPropagation()}
          >
            <Box
              className='row-actions'
              display='flex'
              gap={0.5}
              sx={{ opacity: { xs: 1, md: 0 }, transition: 'opacity 0.15s' }}
            >
              <Button
                size='sm'
                variant='outlined'
                label='Trade'
                onClick={openBlotter}
              />
              <Button
                size='sm'
                variant='text'
                label='View'
                onClick={() => openClientDetails(report)}
              />
            </Box>
          </TableCell>
        </TableRow>

        {open && (
          <PortfolioRowDetail
            report={report}
            onShowTradeBlotter={openBlotter}
          />
        )}

        {/* Trade Blotter Modal */}
        <Dialog
          open={showTradeBlotter && (blotterLoading || strLoading)}
          maxWidth='xs'
        >
          <DialogContent>
            <Box display='flex' alignItems='center' gap={2} py={1}>
              <CircularProgress size={20} />
              <Typography variant='bodyMedium'>
                Loading blotter data…
              </Typography>
            </Box>
          </DialogContent>
        </Dialog>
        {showTradeBlotter && !blotterLoading && !strLoading && (
          <TradeBlotter
            portfolioReport={blotterReport || report}
            onClose={() => setShowTradeBlotter(false)}
            activeSubTradeRequests={activeSubTradeRequests}
            onRefreshActiveSTRs={() => {
              const isGoal = report.modelPortfolioType === 'GOAL';
              const objectId = isGoal ? report.object?.id : report.object?.id;
              fetchActiveSTRs({
                variables: {
                  input: {
                    filter: {
                      ...(isGoal
                        ? { goalId: objectId }
                        : { subAccountId: objectId }),
                      states: ['INITIATED', 'READY', 'REQUESTED'],
                    },
                    pagination: { perPage: 100, offSet: 0 },
                  },
                },
              });
            }}
            onGenerateTrades={async (trades) => {
              try {
                const activeReport = blotterReport || report;
                const holdings = activeReport.holdings || [];
                const defaultSubAccountId =
                  activeReport.subAccounts?.[0]?.subAccount?.id ||
                  activeReport.object?.id;
                const subTradeRequests = trades
                  .filter((t) => t.tradeAmountCents !== 0)
                  .map((t) => {
                    const holding = holdings[t.holdingIndex];
                    const saId =
                      (t as any).subAccountId &&
                      (t as any).subAccountId !== '__agg__'
                        ? (t as any).subAccountId
                        : defaultSubAccountId;
                    return {
                      subAccountId: saId,
                      financialProductId: holding?.financialProduct?.id,
                      portfolioReportId: activeReport.id,
                      type: t.side === 'BUY' ? 'BUY' : 'SELL',
                      moneyAllocatedCents: Math.abs(t.tradeAmountCents),
                      currency: t.currency,
                    };
                  })
                  .filter((r) => r.financialProductId);

                if (subTradeRequests.length === 0) return;

                const source =
                  activeReport.modelPortfolioType === 'GOAL'
                    ? 'GOAL'
                    : 'SUB_ACCOUNT';
                const sourceId = activeReport.object?.id;

                await createSubTradeRequests({
                  variables: {
                    input: {
                      source,
                      sourceId,
                      subTradeRequests,
                    },
                  },
                });

                if (bulkTradingProcess === 'BUCKET') {
                  if (sourceId) {
                    try {
                      await bucketSubTradeRequests({
                        variables: {
                          input: {
                            source,
                            sourceId,
                            ...(organizationId && {
                              organizationId,
                            }),
                          },
                        },
                      });
                    } catch (bucketErr) {
                      console.warn(
                        'Bucketing failed (trades were still created):',
                        bucketErr,
                      );
                    }
                  }
                }

                showToast({
                  severity: 'success',
                  message: `${subTradeRequests.length} trade${subTradeRequests.length !== 1 ? 's' : ''} created successfully. View them in the Trades tab.`,
                });
                setShowTradeBlotter(false);
                onTradeComplete();
              } catch {
                showToast({
                  severity: 'error',
                  message: 'Failed to create trades. Please try again.',
                });
              }
            }}
          />
        )}
      </>
    );
  },
);

export default PortfolioRow;
